import {
  Button,
  Box,
  CircularProgress,
  Slide,
  Typography,
} from "@mui/material";
import {
  NavLink,
  Outlet,
  useParams,
  generatePath,
  useSearchParams,
} from "react-router-dom";
import { useCallback, useEffect, useState } from "react";

import Form from "./Form";
import SearchLoading from "./SearchLoading";
import DomainFinder from "./DomainFinder";
import WebsiteStatusCheck from "./WebsiteStatusCheck";
import UnauthorizedErrorDisplay from "../UnauthorizedErrorDisplay";
import UnsuccessfulErrorDisplay from "../UnsuccessfulErrorDisplay";

import APIResponse from "../../types/APIResponse";

import NetworkFailureToast from "../NetworkFailureToast";
import { useTrueBizApi } from "../../api";
import ResultsTabs from "../ResultsTabs";
import APICompanyMatchProfile from "../../types/APICompanyMatchProfile";
import getAdminSetting from "../../utilities/getAdminSetting";

export function LookupDomainWithoutDataLoader({
  matchProfiles,
  selectedMatchProfileId,
  onMatchProfileChange,
}: {
  matchProfiles: APICompanyMatchProfile[] | null;
  selectedMatchProfileId: string | null;
  onMatchProfileChange: (id: string | null) => void;
}) {
  const api = useTrueBizApi();
  const { domain } = useParams();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  let [searchParams, _] = useSearchParams();

  // change disabled to be set via useEffect on matchProfiles === null
  const [disableForm, setDisableForm] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [failed, setFailed] = useState<boolean>(false);
  const [response, setResponse] = useState<null | APIResponse>(null);
  const [lastRequestStatus, setLastRequestStatus] = useState<null | number>(
    null
  );
  const [calledOnce, setCalledOnce] = useState<boolean>(false);

  const [showWebsiteStatusCheck, setShowWebsiteStatusCheck] =
    useState<boolean>(false);

  const [showDomainFinder, setShowDomainFinder] = useState<boolean>(false);

  const onResetState = () => {
    onMatchProfileChange(null);

    setResponse(null);
    setLastRequestStatus(null);
  };

  const onResetFailed = () => {
    setFailed(false);
  };

  const onSubmitLookup = useCallback(
    async (domain: string) => {
      setCalledOnce(true);
      setLoading(true);
      setFailed(false);

      try {
        setResponse(await api.domainLookup(domain, selectedMatchProfileId));
        setLastRequestStatus(200);
      } catch (e) {
        if (api.isResponseError(e)) {
          setLastRequestStatus(e.status);
          setResponse(await e.json());
        }
        setFailed(true);
      } finally {
        setLoading(false);
      }
    },
    [api, selectedMatchProfileId]
  );

  const pathPrefix = generatePath("/domain/:domain", {
    domain: encodeURIComponent(String(domain)),
  });

  useEffect(() => {
    // Since this component is shown for both the index route (no domain) and
    // when a domain is entered after searching, and since the user can navigate
    // from a search back to the index route, we need to handle what happens
    // when the domain is cleared but the component stays mounted.
    if (!domain) {
      setResponse(null);
      setLastRequestStatus(null);
    }
  }, [domain]);

  useEffect(() => {
    setDisableForm(matchProfiles === null);
  }, [matchProfiles]);

  useEffect(() => {
    if (searchParams.get("run") !== null && domain && calledOnce === false) {
      onSubmitLookup(domain);
    }
  }, [searchParams, domain, onSubmitLookup, calledOnce]);

  return (
    <Box sx={{ zIndex: 1203 }}>
      <SearchLoading loading={loading} />

      <NetworkFailureToast open={failed} onClose={onResetFailed} />

      <Box display="flex" alignItems="flex-end" justifyContent="space-between">
        <Typography variant="h4" component="h1" fontWeight={700}>
          Web Presence Review
        </Typography>

        <div>
          <Button
            sx={{ mr: 2, width: 190 }}
            variant="outlined"
            onClick={() => setShowWebsiteStatusCheck(true)}
            size="small"
          >
            Website Status Check
          </Button>

          {getAdminSetting("domainFinder") === true && (
            <Button
              sx={{ ml: 2, width: 190 }}
              variant="outlined"
              onClick={() => setShowDomainFinder(true)}
              size="small"
            >
              Domain Finder
            </Button>
          )}
        </div>
      </Box>

      <Form
        disabled={disableForm}
        onSubmitSearch={onSubmitLookup}
        loading={loading}
        matchProfiles={matchProfiles || []}
        selectedMatchProfileId={selectedMatchProfileId}
        onMatchProfileChange={onMatchProfileChange}
      />

      <UnsuccessfulErrorDisplay
        lastRequestStatus={lastRequestStatus}
        callToActionComponent={
          <NavLink to="json">View the raw JSON response</NavLink>
        }
      />
      <UnauthorizedErrorDisplay
        lastRequestStatus={lastRequestStatus}
        onResetState={onResetState}
      />

      {response && (
        <Slide direction="up" in>
          <Box marginTop={1.5}>
            <ResultsTabs pathPrefix={pathPrefix} response={response} />

            <Outlet context={{ response, onSubmitLookup, domain }} />
          </Box>
        </Slide>
      )}

      <WebsiteStatusCheck
        open={showWebsiteStatusCheck}
        onClose={() => setShowWebsiteStatusCheck(false)}
        onResetState={onResetState}
      />

      <DomainFinder
        open={showDomainFinder}
        onClose={() => setShowDomainFinder(false)}
      />
    </Box>
  );
}

export default function LookupDomain() {
  const api = useTrueBizApi();
  const [matchProfiles, setMatchProfiles] = useState<
    APICompanyMatchProfile[] | null
  >(null);
  const [selectedMatchProfileId, setSelectedMatchProfileId] = useState<
    string | null
  >(null);

  useEffect(() => {
    const loadCompanyMatchProfiles = async () => {
      try {
        const companyMatchProfiles = await api.getCompanyMatchProfiles();
        setMatchProfiles(companyMatchProfiles);
        setSelectedMatchProfileId(
          (id) =>
            id ||
            (companyMatchProfiles && companyMatchProfiles.length >= 1
              ? companyMatchProfiles[0].id
              : null)
        );
      } catch {
        // pass
      }
    };

    loadCompanyMatchProfiles();
  }, [api]);

  return !matchProfiles ? (
    <CircularProgress />
  ) : (
    <LookupDomainWithoutDataLoader
      matchProfiles={matchProfiles}
      selectedMatchProfileId={selectedMatchProfileId}
      onMatchProfileChange={setSelectedMatchProfileId}
    />
  );
}
